// The embed's side of `@hopdrive/feature-order-index`: everything an order surface needs
// from this app, assembled once and handed down as values.
//
// The package cannot import `@hopdrive/feature-portal-products` (features do not import each
// other), so the two contexts meet here instead — the portal context above, the order-index
// configuration below. Every surface underneath reads `useOrderIndex()` and knows nothing
// about the embed's store, its hash session, or its modals.
//
// `EmbedOrderInteraction` sits above the order-index configuration because it carries the interaction
// layer it publishes: one provider per mount means one photo viewer, one set of dialogs, one
// chat for every card below.

import React, { useCallback, useMemo, useState } from 'react';
import { OrderIndexProvider, createOrderIndexPlatform, toOrderRooftopFacts, readMetricParam, withoutMetricParam } from '@hopdrive/feature-order-index';
import type { OrderIndexConfig, OrderTabActionsContribution } from '@hopdrive/feature-order-index';
import { OrderIndexPlatformProvider } from '@hopdrive/feature-order-index';
import type { OrderHangtagProps, OrderIndexPlatform } from '@hopdrive/feature-order-index';
import { useLocation, useNavigate } from 'react-router-dom';
import { capabilityGate, usePortalViewer, useProductEntry, useShellTabActions } from '@hopdrive/feature-portal-products';
import type { TabActionsContribution } from '@hopdrive/feature-portal-products';

import PortalMoveTileHangtag from '@components/MoveTileHangtag';
import type { Hangtags } from '@gql/base-types';
import { copyToClipboard } from '@services/utilityService';
import { useUnreadMessages } from '@features/moves/hooks/useUnreadMessages';
import { useCustomerConfig, useCustomerConfigLoaded, useRooftop } from '@store';
import { getReadOnly } from '@utils/embedSettings';
import { writeAllowed } from '@features/products/embedWriteGate';

import { EmbedOrderInteraction, useEmbedOrderInteraction } from './EmbedOrderInteraction';
import { useMovesOrderOperationalModel } from './useMovesOrderOperationalModel';

/** The embed's hangtag over the package's structural shape; the tag reads the same fields. */
const MoveTileHangtag: React.FC<OrderHangtagProps> = ({ hangtag }) => (
  <PortalMoveTileHangtag hangtag={hangtag as unknown as Hangtags} />
);

/**
 * The embed's platform: the package's portal formatters, the embed's own clipboard (the package
 * default copies silently; this one raises the embed's confirmation toast), and the borrowed
 * hangtag. A module constant, so it is stable for the app's lifetime.
 */
const PLATFORM: OrderIndexPlatform = createOrderIndexPlatform({ MoveTileHangtag, copyToClipboard });

const EmbedOrderIndexConfig: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const viewer = usePortalViewer();
  const rooftopRecord = useRooftop();
  const configLoaded = useCustomerConfigLoaded();
  const referenceNumLabel = (useCustomerConfig()?.reference_num_label as string | null | undefined) ?? null;
  const interaction = useEmbedOrderInteraction();

  // Unread is an id set, not a count: the hook runs its own window and gates itself, so only its
  // set of move ids carries over, as Valet's adapt extras.
  const { unreadMoves } = useUnreadMessages();
  const unreadMoveIds = useMemo(() => new Set(unreadMoves.map(unread => unread.moveId)), [unreadMoves]);
  const adaptExtras = useMemo(() => ({ moves: { unreadMoveIds } }), [unreadMoveIds]);

  // The shell's registration is an effect keyed on the contribution, and the package's tab has no
  // way to call a hook on the app's behalf. So the adapter holds the contribution and publishes a
  // stable setter; the registration itself stays here, where the outlet is.
  const [tabActions, setTabActions] = useState<OrderTabActionsContribution | null>(null);
  const registerTabActions = useCallback((contribution: OrderTabActionsContribution | null) => {
    setTabActions(contribution);
  }, []);
  useShellTabActions(tabActions as TabActionsContribution | null);

  const operationalModel = useMovesOrderOperationalModel();
  const rooftop = useMemo(() => toOrderRooftopFacts(rooftopRecord, operationalModel), [rooftopRecord, operationalModel]);

  // A read_only Moves product (a lapsed contract) shows its orders with no write controls, and so
  // does a read-only iframe; open, chat, photos, and Export CSV stay.
  const location = useLocation();
  const readOnly = getReadOnly(new URLSearchParams(location.search));
  const canManageMoves = writeAllowed(capabilityGate(useProductEntry('moves'), 'manageExisting'), readOnly);
  const writeControlsAllowed = useMemo(() => ({ moves: canManageMoves }), [canManageMoves]);

  // The Orders tab's `?metric=` arrival from a Home figure, read and cleared through this app's router.
  const navigate = useNavigate();
  const metricDeepLink = useMemo(
    () => ({
      metric: readMetricParam(location.search),
      clear: () => navigate({ pathname: location.pathname, search: withoutMetricParam(location.search), hash: location.hash }, { replace: true }),
    }),
    [location.pathname, location.search, location.hash, navigate]
  );

  const value = useMemo<OrderIndexConfig>(
    () => ({
      rooftop,
      configLoaded,
      referenceNumLabel,
      viewer: { role: viewer.role, isAdminViewer: viewer.isAdminViewer, customerId: viewer.customerId ?? null },
      platform: PLATFORM,
      interaction,
      adaptExtras,
      writeControlsAllowed,
      metricDeepLink,
      registerTabActions,
    }),
    [rooftop, configLoaded, referenceNumLabel, viewer.role, viewer.isAdminViewer, viewer.customerId, interaction, adaptExtras, writeControlsAllowed, metricDeepLink, registerTabActions],
  );

  return (
    <OrderIndexProvider value={value}>
      <OrderIndexPlatformProvider platform={PLATFORM}>{children}</OrderIndexPlatformProvider>
    </OrderIndexProvider>
  );
};

/** Composes the embed interaction layer and configuration around a shared order surface. */
const EmbedOrderIndexAdapter: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <EmbedOrderInteraction>
    <EmbedOrderIndexConfig>{children}</EmbedOrderIndexConfig>
  </EmbedOrderInteraction>
);

export default EmbedOrderIndexAdapter;
